import { createPortal } from 'react-dom';
import { theme } from '@frontend/theme';
import type { DotPosition, OffsetStyle } from '../types';
import { dotPositionStyles } from './dot-position-styles';
import { HotspotNudge } from './hotspot-nudge';

type OnboardingTargetNudgeProps = {
  targetElement: Element;
  position: DotPosition;
  offset?: OffsetStyle;
};

/**
 * Renders the hotspot nudge on the actual target element.
 *
 * HTML targets can reuse HotspotNudge directly.
 * SVG targets need an SVG-native nudge because an HTML <span> will not render
 * correctly inside SVG elements like <path>.
 */
export function OnboardingTargetNudge({ targetElement, position, offset }: OnboardingTargetNudgeProps) {
  if (targetElement instanceof SVGGraphicsElement) {
    return <SvgTargetNudge targetElement={targetElement} offset={offset} />;
  }

  if (targetElement instanceof HTMLElement) {
    return createPortal(
      <HotspotNudge className='onboarding-hotspot-nudge' css={dotPositionStyles[position]} offset={offset} />,
      targetElement
    );
  }

  return null;
}

type SvgTargetNudgeProps = {
  targetElement: SVGGraphicsElement;
  offset?: OffsetStyle;
  size?: number;
  color?: string;
};

function SvgTargetNudge({
  targetElement,
  offset,
  size = 16,
  color = theme.colors.secondary.seaweed40,
}: SvgTargetNudgeProps) {
  const svgElement = targetElement.ownerSVGElement;

  if (!svgElement) {
    return null;
  }

  const box = targetElement.getBBox();
  const svgBox = svgElement.viewBox.baseVal;

  const { x, y } = getSvgNudgePosition({
    targetElement,
    box,
    svgBox,
    offset,
    size,
  });

  const outerRadius = size / 2;
  const innerRadius = (size * 0.75) / 2;

  return createPortal(
    <g className='onboarding-hotspot-nudge' pointerEvents='none'>
      <circle cx={x} cy={y} r={outerRadius} fill={theme.colors.secondary.seaweed50} opacity={0.5}>
        <animate attributeName='opacity' values='0.5;0;0.5' dur='2s' begin='0.2s' repeatCount='indefinite' />
      </circle>

      <circle cx={x} cy={y} r={innerRadius} fill={color} />
    </g>,
    svgElement
  );
}

function getSvgNudgePosition({
  targetElement,
  box,
  svgBox,
  offset,
  size,
}: {
  targetElement: SVGGraphicsElement;
  box: SVGRect;
  svgBox: SVGRect;
  offset?: OffsetStyle;
  size: number;
}) {
  const radius = size / 2;
  const padding = 10;

  /**
   * For SVG paths, place the nudge at the actual beginning of the segment.
   *
   * We do not use the bounding box corner because pie/donut segments are curved,
   * and bbox corners can be empty space outside the visible segment.
   */
  const pathStartPoint = getSvgPathStartPoint({
    targetElement,
    distanceFromStart: radius,
  });

  const baseX = pathStartPoint?.x ?? box.x + box.width;
  const baseY = pathStartPoint?.y ?? box.y;

  const minX = svgBox.x + padding + radius;
  const maxX = svgBox.x + svgBox.width - padding - radius;
  const minY = svgBox.y + padding + radius;
  const maxY = svgBox.y + svgBox.height - padding - radius;

  const clampedX = Math.min(Math.max(baseX, minX), maxX);
  const clampedY = Math.min(Math.max(baseY, minY), maxY);

  const offsetX = clampedX + (offset?.left ?? 0) - (offset?.right ?? 0);
  const offsetY = clampedY + (offset?.top ?? 0) - (offset?.bottom ?? 0);

  return {
    x: Math.min(Math.max(offsetX, minX), maxX),
    y: Math.min(Math.max(offsetY, minY), maxY),
  };
}

function getSvgPathStartPoint({
  targetElement,
  distanceFromStart,
}: {
  targetElement: SVGGraphicsElement;
  distanceFromStart: number;
}) {
  if (!(targetElement instanceof SVGGeometryElement)) {
    return undefined;
  }

  const totalLength = targetElement.getTotalLength();

  if (!totalLength) {
    return undefined;
  }

  /**
   * Use a small distance from the start instead of exact 0 so the dot sits
   * slightly inside the visible rounded segment instead of on the edge/gap.
   */
  const point = targetElement.getPointAtLength(Math.min(distanceFromStart, totalLength));

  return {
    x: point.x,
    y: point.y,
  };
}
